//Icons for shipment cards based on the accessorials. Example over weight, over length, etc

import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { getShipmentV4 } from "../../../APIs/Helpers";

const getHuDimensions = (hu, convertToFeet) => {
  const raw = hu.dimensions || {};
  const toFeet = (value) => (convertToFeet && typeof value === "number" ? value / 12 : value);
  return { length: toFeet(raw.length), width: toFeet(raw.width), height: toFeet(raw.height) };
};

const capitalize = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");

// Fixed column widths shared by both tables so the Qty column lines up
// vertically across every Handling Units / Items table in the modal.
const COL1_WIDTH = "50%";
const QTY_WIDTH = "15%";
const COL3_WIDTH = "35%";
const tableStyle = { tableLayout: "fixed", width: "100%" };

const HandlingUnitsTable = ({ handlingUnits, convertToFeet }) => (
  <table className="table table-striped table-bordered table-sm mb-2" style={tableStyle}>
    <thead>
      <tr>
        <th style={{ width: COL1_WIDTH }}>Packaging</th>
        <th style={{ width: QTY_WIDTH }}>Qty</th>
        <th style={{ width: COL3_WIDTH }}>Dimensions</th>
      </tr>
    </thead>
    <tbody>
      {handlingUnits.map((hu, key) => {
        const dims = getHuDimensions(hu, convertToFeet);
        return (
          <tr key={hu.id || key}>
            <td>{capitalize(hu.packaging)}</td>
            <td>{hu.quantity}</td>
            <td>{dims.length} x {dims.width} x {dims.height}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const ItemsTable = ({ items }) => (
  <table className="table table-striped table-bordered table-sm mb-2" style={tableStyle}>
    <thead>
      <tr>
        <th style={{ width: COL1_WIDTH }}>Description</th>
        <th style={{ width: QTY_WIDTH }}>Qty</th>
        <th style={{ width: COL3_WIDTH }}>Total Weight</th>
      </tr>
    </thead>
    <tbody>
      {items.map((item, key) => (
        <tr key={item.id || key}>
          <td>{item.description}</td>
          <td>{item.quantity}</td>
          <td>{item.weightTotal ?? item.weight} {item.weightUOM}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

const HandlingUnitsAndItems = ({ handlingUnits, convertToFeet }) => {
  const items = handlingUnits.flatMap(hu => hu.items || []);
  return (
    <>
      {handlingUnits.length ?
        <>
          <div>Handling Units ({handlingUnits.length})</div>
          <HandlingUnitsTable handlingUnits={handlingUnits} convertToFeet={convertToFeet} />
        </>
        : null}
      {items.length ?
        <>
          <div>Items ({items.length})</div>
          <ItemsTable items={items} />
        </>
        : null}
    </>
  );
};

// Multi-stop consolidations list handling units/items per order (deduped, since
// the same order appears at both its pickup and delivery stop).
const getOrders = (shipmentV4) => {
  const ordersById = new Map();
  (shipmentV4.stops || []).forEach(stop => {
    (stop.orders || []).forEach(order => {
      if (!ordersById.has(order.id)) {
        ordersById.set(order.id, order);
      }
    });
  });
  return Array.from(ordersById.values());
};

const Packages = ({ render, shipment }) => {
  const carrierClientId = localStorage.getItem("carrierClientId");
  // Schuff/graywolf require dimensions in feet rather than inches
  const convertToFeet = carrierClientId === "Schuff" || carrierClientId === "graywolf";

  const [showModal, setShowModal] = useState(false);
  const [shipmentV4, setShipmentV4] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const shipmentId = shipment?.id;

  // Only fetch the v4 shipment once the modal is opened (and only once per card)
  useEffect(() => {
    if (!showModal || shipmentV4 || !shipmentId) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    getShipmentV4(shipmentId).then(result => {
      if (cancelled) return;
      if (result && result.id) {
        setShipmentV4(result);
      } else {
        setError(true);
      }
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [showModal, shipmentV4, shipmentId]);

  if (render !== "show" || !shipment) {
    return null;
  }

  const renderBody = () => {
    if (loading) {
      return (
        <div className="text-center my-3">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      );
    }
    if (error) {
      return <div className="text-danger">Unable to load handling units &amp; items. Please try again.</div>;
    }
    if (!shipmentV4) {
      return null;
    }

    const orders = getOrders(shipmentV4);
    if (orders.some(order => order.handlingUnits?.length)) {
      return orders.map((order, idx) => (
        <div key={order.id} className={idx > 0 ? "mt-3" : ""}>
          <div className="font-weight-bold">Order: {order.usercontrol || order.id}</div>
          <HandlingUnitsAndItems handlingUnits={order.handlingUnits || []} convertToFeet={convertToFeet} />
        </div>
      ));
    }

    const handlingUnits = shipmentV4.handlingUnits || [];
    if (!handlingUnits.length) {
      return <div>No handling units or items on this shipment.</div>;
    }
    return <HandlingUnitsAndItems handlingUnits={handlingUnits} convertToFeet={convertToFeet} />;
  };

  return (
    <div>
      <button
        className="mt-2 btn btn-link p-0"
        onClick={() => { setShowModal(true) }}>
        Handling Units &amp; Items
      </button>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Handling Units &amp; Items</Modal.Title>
        </Modal.Header>
        <Modal.Body className="cardBodyRow2">
          {renderBody()}
        </Modal.Body>
      </Modal>
    </div>
  );
};
export default Packages;
